import React, { useEffect, useState, useRef } from "react";
import {
    Card,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Box,
    Typography,
    Stack,
    Button
} from "@mui/material";
import Iconify from "../components/iconify";


import { get } from "src/utils/networkUtils";

export default function WeeklyLeaderboard({
    selectedStagePeriod,
    selectedStagePeriodId,
    tenantId,
    payPrizeHandler
}) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const limit = 20;
    const scrollRef = useRef();

    useEffect(() => {
        setLeaderboard([]);
        setOffset(0);
        setHasMore(true);
        if (!selectedStagePeriodId || selectedStagePeriodId === "all") return;
        fetchLeaderboard(0, true);
        // eslint-disable-next-line
    }, [selectedStagePeriodId, tenantId]);

    const fetchLeaderboard = async (offsetValue = 0, reset = false) => {
        if (reset) {
            setLoading(true);
        }
        try {
            const response = await get(
                `/admin/stagePeriodLeaderboard?stagePeriodId=${selectedStagePeriodId}&type=global&offset=${offsetValue}&limit=${limit}&tenantId=${tenantId}`
            );
            const newRows = response.leaderboard || [];
            setLeaderboard((prev) => reset ? newRows : [...prev, ...newRows]);
            setHasMore(newRows.length === limit);
            setOffset(offsetValue + limit);
        } finally {
            if (reset) {
                setLoading(false);
            }
        }
    };

    // Infinite scroll handler with pagination loader
    const [paginationLoading, setPaginationLoading] = useState(false);
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (hasMore && !loading && !paginationLoading && scrollHeight - scrollTop - clientHeight < 100) {
            setPaginationLoading(true);
            fetchLeaderboard(offset, false).then(() => setPaginationLoading(false)).catch(() => setPaginationLoading(false));
        }
    };

    return (
        <Card sx={{ mt: 4 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 2 }}
            >
                <Typography variant="h6">Stage Period Leaderboard</Typography>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:cloud-upload-outline" />}
                    onClick={payPrizeHandler}
                    disabled={
                        !selectedStagePeriod ||
                        selectedStagePeriod?.isPaid ||
                        new Date(selectedStagePeriod?.endDate) > new Date() ||
                        loading ||
                        selectedStagePeriodId === "all"
                    }
                >
                    Pay Prizes
                </Button>
            </Stack>
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    maxHeight: 500,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                    },
                }}
            >
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="weekly leaderboard table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>User ID</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Coins</TableCell>
                                <TableCell>Custom Prize</TableCell>
                                <TableCell>Points</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaderboard.map((row, index) => (
                                <TableRow
                                    key={`${row.userId}-${index}`}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    <TableCell>{row.rank}</TableCell>
                                    <TableCell>{row.userId}</TableCell>
                                    <TableCell>
                                        <a href={`/dashboard/profile?user_id=${row.userId}`}>{row.name}</a>
                                    </TableCell>
                                    <TableCell>{row.draftWinnings}</TableCell>
                                    <TableCell>{row.draftCustomPrizeWinnings}</TableCell>
                                    <TableCell>{row.score}</TableCell>
                                </TableRow>
                            ))}
                            {loading && leaderboard.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {paginationLoading && leaderboard.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Card>
    );
}
